import { useState } from "react";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";
import type { Role } from "@/lib/api/admin-api";
import type { RoleScopeAssignment } from "@/types/user";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SCOPE_ORDER: RoleScopeAssignment["scopeType"][] = [
  "ORGANIZATION",
  "COMPANY",
  "REGION",
  "LOCATION",
];

const SCOPE_LABELS: Record<RoleScopeAssignment["scopeType"], MessageDescriptor> = {
  ORGANIZATION: msg`Organization`,
  // The scope key stays COMPANY — the model rename is a separate change — but
  // the word users read is Concept (T-106).
  COMPANY: msg`Concept`,
  REGION: msg`Region`,
  LOCATION: msg`Location`,
};

interface ScopeAssignmentsTableProps {
  roles: Role[];
  assignments: RoleScopeAssignment[];
  onRoleClick?: (roleId: number) => void;
}

function FilterPills({
  options,
  selected,
  onSelect,
}: {
  options: { value: string; label: string }[];
  selected: Set<string>;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(({ value, label }) => {
        const active = selected.has(value);
        return (
          <button
            key={value}
            type="button"
            onClick={() => onSelect(value)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors capitalize ${
              active
                ? "bg-(--gray-900) text-(--brand-white) border-(--gray-900)"
                : "bg-(--color-surface-content) text-(--color-text-secondary) border-(--color-border) hover:border-(--color-border-strong)"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function ScopeAssignmentsTable({
  roles,
  assignments,
  onRoleClick,
}: ScopeAssignmentsTableProps) {
  const { t } = useLingui();
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set(["all"]));
  const [selectedScopeTypes, setSelectedScopeTypes] = useState<Set<string>>(new Set(["all"]));

  const toggleFilter = (
    value: string,
    current: Set<string>,
    set: (s: Set<string>) => void,
  ) => {
    if (value === "all") {
      set(new Set(["all"]));
      return;
    }
    const next = new Set(current);
    next.delete("all");
    if (next.has(value)) {
      next.delete(value);
      if (next.size === 0) next.add("all");
    } else {
      next.add(value);
    }
    set(next);
  };

  const roleOptions = [
    { value: "all", label: t`All roles` },
    ...roles.map((r) => ({ value: String(r.id), label: r.name })),
  ];

  const scopeTypeOptions = [
    { value: "all", label: t`All scopes` },
    ...SCOPE_ORDER.map((st) => ({ value: st, label: t(SCOPE_LABELS[st]) })),
  ];

  const visible = assignments
    .filter((a) => {
      const roleMatch = selectedRoles.has("all") || selectedRoles.has(String(a.roleId));
      const scopeMatch = selectedScopeTypes.has("all") || selectedScopeTypes.has(a.scopeType);
      return roleMatch && scopeMatch;
    })
    .sort((a, b) => {
      const scopeDiff = SCOPE_ORDER.indexOf(a.scopeType) - SCOPE_ORDER.indexOf(b.scopeType);
      if (scopeDiff !== 0) return scopeDiff;
      return a.roleName.localeCompare(b.roleName);
    });

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-(--color-text-secondary) uppercase tracking-wider">
            <Trans>Roles</Trans>
          </span>
          <FilterPills
            options={roleOptions}
            selected={selectedRoles}
            onSelect={(v) => toggleFilter(v, selectedRoles, setSelectedRoles)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-(--color-text-secondary) uppercase tracking-wider">
            <Trans>Scope Type</Trans>
          </span>
          <FilterPills
            options={scopeTypeOptions}
            selected={selectedScopeTypes}
            onSelect={(v) => toggleFilter(v, selectedScopeTypes, setSelectedScopeTypes)}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="bg-(--color-surface-muted) hover:bg-(--color-surface-muted)">
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary)">
                <Trans>Role</Trans>
              </TableHead>
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary)">
                <Trans>Scope Type</Trans>
              </TableHead>
              <TableHead className="h-auto px-4 py-3 text-left font-medium text-(--color-text-secondary)">
                <Trans>Scope Name</Trans>
              </TableHead>
              <TableHead className="h-auto px-4 py-3 text-center font-medium text-(--color-text-secondary)">
                <Trans>Users</Trans>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((a) => (
              <TableRow
                key={`${a.roleId}-${a.scopeType}-${a.scopeId}`}
                className="last:border-b-0 hover:bg-(--color-surface-muted)/50"
              >
                <TableCell className="px-4 py-3 font-medium">
                  {onRoleClick ? (
                    <button
                      type="button"
                      onClick={() => onRoleClick(a.roleId)}
                      className="hover:underline underline-offset-2 text-left"
                    >
                      {a.roleName}
                    </button>
                  ) : (
                    a.roleName
                  )}
                </TableCell>
                <TableCell className="px-4 py-3">
                  <Badge variant="neutral" className="font-mono text-xs">
                    {t(SCOPE_LABELS[a.scopeType])}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-3 text-(--color-text-secondary)">{a.scopeName}</TableCell>
                <TableCell className="px-4 py-3 text-center">
                  <span className="inline-flex items-center gap-1 text-xs text-(--color-text-secondary)">
                    <Users className="h-3 w-3" />
                    {a.userCount}
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="px-4 py-16 text-center text-sm text-(--color-text-secondary)">
                  {assignments.length === 0
                    ? t`No scope assignments found. Assign roles to users at specific scopes to see them here.`
                    : t`No assignments match the selected filters.`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
